import { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useUpdateInSettingsMutation } from "@/pages/Private/redux/company/company.api";

import { InputField } from "@/components/InputField/InputField";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils/errors";

import {
	CompanyType,
	UpdateCompanyInSettings,
	UpdateCompanyInSettingsSchema,
} from "../schema/company";

type CompanyInformationProps = {
	company?: CompanyType;
};

export const CompanyInformation: FunctionComponent<CompanyInformationProps> = ({ company }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.${key}`);

	const [updateCompany, { isLoading, error }] = useUpdateInSettingsMutation();

	const initialState: Partial<UpdateCompanyInSettings> = {
		name: company?.name || "",
		linkedinUrl: company?.linkedinUrl || "",
		website: company?.website || "",
	};

	const {
		reset,
		handleSubmit,
		setValue,
		formState: { errors },
		watch,
	} = useForm<UpdateCompanyInSettings>({
		defaultValues: initialState,
		resolver: zodResolver(UpdateCompanyInSettingsSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (company) {
			reset({
				name: company?.name || "",
				linkedinUrl: company?.linkedinUrl || "",
				website: company?.website || "",
			});
		}
	}, [company]);

	const onSubmit = async (values: UpdateCompanyInSettings) => {
		await updateCompany(values).unwrap();
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">{ts("companyInformation")}</div>

			<div className="flex flex-row gap-4 w-full mb-1">
				<InputField
					error={!!errors.name?.message}
					handleChange={(e) => {
						setValue("name", e.target.value);
					}}
					label={"Company name"}
					name="companyName"
					placeholder={ts("name of your company")}
					value={formValues?.name || ""}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-1">
				<InputField
					error={!!errors.linkedinUrl?.message}
					handleChange={(e) => {
						setValue("linkedinUrl", e.target.value);
					}}
					label={"LinkedIn URL"}
					name="linkedinUrl"
					placeholder={"linkedin url of your company"}
					value={formValues?.linkedinUrl || ""}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-1">
				<InputField
					error={!!errors.website?.message}
					handleChange={(e) => {
						setValue("website", e.target.value);
					}}
					label={"Website"}
					name="website"
					placeholder={"website of your company"}
					value={formValues?.website || ""}
				/>
			</div>

			{getAllErrors(error, formErrors).length
				? renderErrorMessages(getAllErrors(error, formErrors))
				: null}

			<div className="flex flex-row justify-end w-full">
				<div className="w-auto">
					<Button
						color={ButtonColor.ACTION}
						isLoading={isLoading}
						size={ButtonSize.S}
						title={t("basics.save")}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
